/**
 * pharmacyOptions: the ONE place the website reads a pharmacy product's
 * option groups and applies their selection rules. Mirrors the apps
 * (app/utils/pharmacyOptions.js and app/utility/dishOptions.js) so a pick
 * behaves the same on every surface.
 *
 * The server's shape (models/optionModel.js), as GET
 * /healthcare/healthcare-product/:id populates it:
 *   optionGroups: [{ _id, name, minOptions, maxOptions, kind,
 *                    options: [{ _id, option: { _id, name, price, enabled } }] }]
 * The storefront read (GET /healthcare/healthcare-vendor/:id) lists the
 * groups as bare ids only, so the product dialog reads the product first.
 *
 * Group rules:
 *   minOptions  0 = optional, N = the shopper must pick at least N
 *   maxOptions  1 = pick one (radio), N = up to N, 0 = no upper limit
 *
 * A pick is stored on the cart line, and sent on the order line, as
 * { groupId, optionId, name, price }. Prices are what the product data says;
 * nothing here works one out.
 */

const num = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const idOf = (value) =>
  value && typeof value === "object" ? String(value._id || "") : value ? String(value) : "";
const priceOf = (value) => Math.max(0, num(value));
const isGroupObject = (group) => Boolean(group) && typeof group === "object" && Array.isArray(group.options);

/* Selection rules */

/** The rule a group applies, in one place. */
export const groupRule = (group) => {
  const min = Math.max(0, num(group?.minOptions, 0));
  const rawMax = num(group?.maxOptions, 0);
  const max = rawMax > 0 ? rawMax : Infinity;
  return { min, max, required: min > 0, single: max === 1, unlimited: max === Infinity };
};

/** Short wording for the group header. */
export const groupRuleLabel = (group) => {
  const { min, max, required, single, unlimited } = groupRule(group);
  if (single) return required ? "Pick 1" : "Pick 1 (optional)";
  if (unlimited) return required ? `Pick at least ${min}` : "Pick any";
  if (required) return min === max ? `Pick ${min}` : `Pick ${min} to ${max}`;
  return `Pick up to ${max}`;
};

/**
 * One tap on an option: a picked option comes off, a pick-one group swaps,
 * and a group at its maximum takes nothing more.
 * @returns {object[]} the group's next picks (a new array)
 */
export const toggleOptionInGroup = (current = [], option, group) => {
  const { max, single } = groupRule(group);
  const picked = current.some((o) => idOf(o) === idOf(option));
  if (picked) return current.filter((o) => idOf(o) !== idOf(option));
  if (single) return [option];
  if (current.length >= max) return current;
  return [...current, option];
};

/** Has this group been picked enough? */
export const groupSatisfied = (group, picks = []) => picks.length >= groupRule(group).min;

/** Every group satisfied? */
export const allGroupsSatisfied = (groups = [], selections = {}) =>
  (groups || []).every((group) => groupSatisfied(group, selections[group?._id] || []));

/** Can one more option be picked in this group? */
export const groupAtCap = (group, picks = []) => picks.length >= groupRule(group).max;

/* The pharmacy product's groups */

/** Does the product name any option groups (populated, or as bare ids)? */
export const hasPharmacyOptions = (product) =>
  Array.isArray(product?.optionGroups) && product.optionGroups.length > 0;

/** Are the groups already populated with their options? */
export const pharmacyOptionGroupsLoaded = (product) =>
  hasPharmacyOptions(product) && product.optionGroups.every(isGroupObject);

/**
 * The groups a shopper can pick from, normalised. Switched-off options and
 * VARIANT library groups (whose prices are ignored by design) are left out,
 * and so is a group with nothing left to pick.
 */
export const pharmacyOptionGroupsOf = (product) =>
  (Array.isArray(product?.optionGroups) ? product.optionGroups : [])
    .filter((group) => isGroupObject(group) && group.kind !== "VARIANT")
    .map((group) => ({
      _id: idOf(group),
      name: String(group.name || ""),
      minOptions: Math.max(0, num(group.minOptions)),
      maxOptions: Math.max(0, num(group.maxOptions)),
      options: group.options
        .map((entry) => (entry && entry.option && typeof entry.option === "object" ? entry.option : entry))
        .filter((option) => option && typeof option === "object" && option._id && option.enabled !== false)
        .map((option) => ({ _id: idOf(option), name: String(option.name || ""), price: priceOf(option.price) })),
    }))
    .filter((group) => group._id && group.options.length > 0);

/** The picks as the cart line and the order line carry them, in group order. */
export const pharmacyLineOptions = (groups, selections) =>
  (Array.isArray(groups) ? groups : []).flatMap((group) =>
    (Array.isArray(selections?.[group._id]) ? selections[group._id] : []).map((option) => ({
      groupId: group._id,
      optionId: idOf(option),
      name: String(option?.name || ""),
      price: priceOf(option?.price),
    }))
  );

/** A cart line's picks back as selections; picks no longer offered are dropped. */
export const selectionsFromLineOptions = (groups, lineOptions) => {
  const picked = Array.isArray(lineOptions) ? lineOptions : [];
  const selections = {};
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    const chosen = group.options.filter((option) =>
      picked.some((line) => String(line?.groupId) === group._id && String(line?.optionId) === option._id)
    );
    if (chosen.length) selections[group._id] = chosen;
  });
  return selections;
};

/** What the picks add per unit. */
export const optionsTotal = (options = []) =>
  (Array.isArray(options) ? options : []).reduce((sum, option) => sum + priceOf(option?.price), 0);
